import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { Page, PageHeader } from '../../components/Layout';
import { Badge } from '../../components/Badge';
import { FullPageSpinner } from '../../components/Spinner';
import { ProfileTab } from './ProfileTab';
import { LessonsTab } from './LessonsTab';
import { ScheduleTab } from './ScheduleTab';
import { tokenStore } from '../../lib/token-store';
import { BookTab } from './BookTab';

type TabId = 'lessons' | 'profile' | 'schedule' | 'book';

/**
 * What support sees while acting as someone.
 *
 * Everything on this page goes through the ordinary student/tutor
 * endpoints with the impersonation token — there is no admin-only shortcut
 * behind any of it. If a tutor cannot see their own availability, support
 * sees exactly the same empty list here, which is the entire point.
 */
export function ActingAsPage() {
  const { user, ready, isImpersonating } = useAuth();
  const [tab, setTab] = useState<TabId>('lessons');

  if (!ready) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isImpersonating) return <Navigate to="/users" replace />;

  const isTutor = user.role === 'tutor';
  const isStudent = user.role === 'student';

  const tabs: { id: TabId; label: string; show: boolean }[] = [
    { id: 'lessons', label: 'Lessons', show: true },
    { id: 'profile', label: 'Profile', show: true },
    { id: 'schedule', label: 'Their calendar', show: isTutor },
    { id: 'book', label: 'Book a lesson', show: isStudent },
  ];

  return (
    <Page>
      <PageHeader
        title={user.fullName}
        description={`Support workspace for this ${user.role}. Open the user app for their complete experience.`}
        actions={<Badge tone="live">Acting as {user.role}</Badge>}
      />

      <div className="mb-6 rounded-md border border-rule bg-surface-2 p-4">
        <p className="mb-3 text-sm text-ink-2">
          Browse all pages with this user's navigation, profile, lessons and permissions.
          Stop impersonating in both windows when you finish.
        </p>
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white"
          onClick={() => {
            const appUrl = import.meta.env.VITE_APP_URL as string | undefined;
            const origin = appUrl ?? (import.meta.env.DEV ? 'http://localhost:3100' : 'https://edubuddy.tech');
            const target = new URL('/impersonate', origin);
            target.hash = new URLSearchParams({ token: tokenStore.getAccessToken() ?? '' }).toString();
            window.open(target.toString(), '_blank', 'noopener,noreferrer');
          }}
        >
          Open full {user.role} app
        </button>
      </div>

      <div
        role="tablist"
        aria-label="Impersonation views"
        className="mb-4 flex gap-1 border-b border-rule"
      >
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-3 py-2 text-[0.8125rem] transition-colors ${
                tab === t.id
                  ? 'border-accent font-medium text-ink'
                  : 'border-transparent text-ink-3 hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
      </div>

      {tab === 'lessons' && <LessonsTab user={user} />}
      {tab === 'profile' && <ProfileTab user={user} />}
      {tab === 'schedule' && isTutor && <ScheduleTab />}
      {tab === 'book' && isStudent && <BookTab />}
    </Page>
  );
}
